import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { capture, type NoteInaccuracyType } from '@/analytics';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Menu, type MenuData } from '@flintmint/web-shared/components/menu';

import { OtherReasonForm } from './other-reason-form';
import { hasSubmittedFeedback, markFeedbackSubmitted } from './message-feedback-store';

import styles from './assistant-message-feedback.module.scss';

type FeedbackPhase = 'controls' | 'thanks' | 'done';

export type FeedbackDocumentKind = 'note' | 'block';

const THANKS_VISIBLE_MS = 3000;
const feedbackSwapMotion = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { type: 'spring' as const, duration: 0.3, bounce: 0 },
};

const PREDEFINED_INACCURACY_OPTIONS: { type: Exclude<NoteInaccuracyType, 'other'>; label: string }[] = [
  { type: 'wrong_medication', label: 'Wrong Medication' },
  { type: 'wrong_diagnosis', label: 'Wrong Diagnosis' },
  { type: 'wrong_history', label: 'Wrong History' },
  { type: 'hallucinated_detail', label: 'Hallucinated Detail' },
  { type: 'formatting', label: 'Formatting' },
];

export const AssistantMessageFeedback = ({
  requestId,
  messageId,
  documentKind = 'note',
}: {
  requestId: string;
  messageId: string;
  documentKind?: FeedbackDocumentKind;
}) => {
  const [phase, setPhase] = useState<FeedbackPhase>(() => (hasSubmittedFeedback(messageId) ? 'done' : 'controls'));

  useEffect(() => {
    if (phase !== 'thanks') return;

    const timeoutId = setTimeout(() => setPhase('done'), THANKS_VISIBLE_MS);
    return () => clearTimeout(timeoutId);
  }, [phase]);

  const captureFlagged = (accurate: boolean, inaccuracyType?: NoteInaccuracyType, freeText?: string) => {
    const shared = {
      accurate,
      ...(inaccuracyType ? { inaccuracy_type: inaccuracyType } : {}),
      ...(freeText ? { free_text: freeText } : {}),
    };

    if (documentKind === 'block') {
      return capture('block_flagged', { block_id: requestId, ...shared });
    }

    capture('note_flagged', { note_id: requestId, ...shared });
  };

  const handleThumbsUp = () => {
    if (phase !== 'controls') return;

    captureFlagged(true);
    markFeedbackSubmitted(messageId);
    setPhase('thanks');
  };

  const submitInaccuracy = (inaccuracyType: NoteInaccuracyType, freeText?: string) => {
    if (phase !== 'controls') return;

    captureFlagged(false, inaccuracyType, freeText);
    markFeedbackSubmitted(messageId);
    setPhase('thanks');
  };

  const reasonMenu: MenuData = [
    {
      type: 'group',
      name: 'What went wrong?',
    },
    ...PREDEFINED_INACCURACY_OPTIONS.flatMap(({ type, label }, index) => [
      ...(index > 0 ? [{ type: 'divider' as const }] : []),
      {
        name: label,
        onClick: () => submitInaccuracy(type),
      },
    ]),
    { type: 'divider' },
    {
      type: 'custom-view',
      name: 'Other',
      customViewTitle: 'What went wrong?',
      renderer: ({ transitionEnded }: { transitionEnded: boolean }) => (
        <OtherReasonForm autoFocus={transitionEnded} onSubmit={(freeText) => submitInaccuracy('other', freeText)} />
      ),
    },
  ];

  return (
    <div data-testid="assistant-message-feedback" data-phase={phase}>
      <AnimatePresence mode="popLayout" initial={false}>
        {phase === 'thanks' ? (
          <motion.div key="thanks" className={styles.feedbackThanks} aria-live="polite" {...feedbackSwapMotion}>
            Thanks for your feedback!
          </motion.div>
        ) : phase === 'controls' ? (
          <motion.div key="actions" className={styles.feedbackActions} {...feedbackSwapMotion}>
            <button type="button" aria-label="Thumbs up" onClick={handleThumbsUp} className={styles.actionButton}>
              <Icon type="thumbs-up" size={20} />
            </button>
            <Popover
              content={<Menu data={reasonMenu} />}
              placement="top"
              subtleShadow
              cornerRadius="smooth"
              offset={[0, 8]}
              maxWidth={280}
            >
              <button type="button" aria-label="Thumbs down" className={styles.actionButton}>
                <Icon type="thumbs-down" size={20} />
              </button>
            </Popover>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
